import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { documentService } from '../../api/services';
import {
  Document,
  FactureElectroniqueStatus,
  StatutFactureElectronique,
} from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';

interface Props {
  document: Document;
  onChanged?: () => void;
  className?: string;
}

const statusBadge: Record<StatutFactureElectronique, string> = {
  BROUILLON: 'bg-gray-100 text-gray-700',
  GENEREE:   'bg-amber-100 text-amber-800',
  TRANSMISE: 'bg-blue-100 text-blue-800',
  ACCEPTEE:  'bg-emerald-100 text-emerald-800',
  REJETEE:   'bg-red-100 text-red-800',
};

const EInvoiceSection: React.FC<Props> = ({ document, onChanged, className }) => {
  const { t } = useLanguage();
  const [status, setStatus] = useState<FactureElectroniqueStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [showXml, setShowXml] = useState(false);

  const load = () =>
    documentService
      .getEInvoiceStatus(document.id)
      .then(setStatus)
      .catch(() => setStatus(null));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document.id]);

  const generate = async () => {
    setBusy(true);
    try {
      const s = await documentService.generateTeif(document.id);
      setStatus(s);
      toast.success(t.documents.eInvoiceGenerated);
      onChanged?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t.documents.eInvoiceError);
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const s = await documentService.submitToTtn(document.id);
      setStatus(s);
      toast.success(t.documents.eInvoiceSubmitted);
      onChanged?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t.documents.eInvoiceError);
    } finally {
      setBusy(false);
    }
  };

  const statut: StatutFactureElectronique =
    status?.statutFactureElectronique ||
    document.statutFactureElectronique ||
    'BROUILLON';
  const canGenerate = statut === 'BROUILLON' || statut === 'REJETEE';
  const canSubmit = statut === 'GENEREE' || statut === 'REJETEE';
  const xml = status?.teifXml;

  return (
    <div className={`card ${className || ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <span>📤</span> {t.documents.eInvoiceTitle}
          </h2>
          <p className="text-sm text-gray-500 mt-1">{t.documents.eInvoiceSubtitle}</p>
        </div>
        <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${statusBadge[statut]}`}>
          {t.documents.eInvoiceStatuts[statut] || statut}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        <div>
          <div className="text-xs text-gray-500">{t.documents.eInvoiceReference}</div>
          <div className="font-mono mt-0.5">{status?.referenceTtn || '—'}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">{t.documents.eInvoiceTransmittedAt}</div>
          <div className="mt-0.5">
            {status?.dateTransmissionTtn
              ? new Date(status.dateTransmissionTtn).toLocaleString()
              : '—'}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500">{t.documents.eInvoiceMessage}</div>
          <div className="mt-0.5">{status?.messageTtn || '—'}</div>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <button
          className="btn-secondary disabled:opacity-50"
          disabled={busy || !canGenerate}
          onClick={generate}
        >
          {busy ? '…' : t.documents.eInvoiceGenerate}
        </button>
        <button
          className="btn-primary disabled:opacity-50"
          disabled={busy || !canSubmit}
          onClick={submit}
        >
          {busy ? '…' : t.documents.eInvoiceSubmit}
        </button>
        {xml && (
          <button className="btn-secondary" onClick={() => setShowXml((v) => !v)}>
            {showXml ? t.documents.eInvoiceHideXml : t.documents.eInvoiceShowXml}
          </button>
        )}
      </div>

      {showXml && xml && (
        <pre className="mt-4 max-h-80 overflow-auto text-xs bg-gray-900 text-gray-100 rounded-lg p-3 whitespace-pre-wrap">
          {xml}
        </pre>
      )}
    </div>
  );
};

export default EInvoiceSection;
